import { toastAlert } from "$lib/commons-web2"
import { captureException } from "@sentry/sveltekit"
import { ErrorContainer, type ErrorDetail } from "@tastyworks/tastyworks-api"
import { _, json } from "svelte-i18n"
import { get } from "svelte/store"
import type { RequireAtLeastOne } from "type-fest"

export class ErrorController {
  isTerminal: boolean = false
  toast?: () => void = undefined

  private get textBundle(): any {
    return get(json)("errors")
  }

  // Handles errors within SvelteKit's load functions and initial rendering
  static handleLoadError({ error }: { error: unknown }) {
    console.error(error)
  }

  // Handles all other synchronous and async errors
  handleError(error: unknown) {
    if (!this.isTerminal) {
      this.isTerminal = error instanceof BaseError ? !error.recoverable : true
    }
    console.error(error)

    if (error instanceof RestError) {
      this.toast = () => alertRestError(error)
    } else if (error instanceof BaseError) {
      this.toast = () =>
        toastAlert("error", this.textBundle.alertTitle, error.message)
    } else {
      this.toast = () =>
        toastAlert(
          "error",
          this.textBundle.alertTitle,
          this.textBundle.other.description
        )
    }

    this.toast()
  }

  static getRestErrorMessage(error: RestError) {
    if (error.errorContainer && !error.errorContainer.isEmpty()) {
      return error.errorContainer.toString()
    } else {
      return error.message
    }
  }
}

class CleanException extends Error {
  stepId: string
  code?: string

  constructor(message: string, stepId: string) {
    super(message)
    this.stepId = stepId
  }
}

export class ExpectedError extends Error {
  displayMessage: string
  code?: string

  constructor(
    message: string,
    options?: { code?: string; displayMessage?: string }
  ) {
    super(message)
    this.displayMessage = options?.displayMessage || message
    this.code = options?.code
  }
}

export function reportAndRecover(error: unknown, stepId: string) {
  let alertTitle: string = get(json)("errors.alertTitle")
  let alertMsg: string = get(json)("errors.other.description")

  const cleaned = new CleanException(alertMsg, stepId)

  if (error instanceof ExpectedError) {
    alertTitle = cleaned.name = error.name
    alertMsg = error.displayMessage

    cleaned.message = error.message
    cleaned.code = error.code
  } else if (error instanceof ErrorContainer) {
    alertTitle = cleaned.name = error.message
    alertMsg = cleaned.message = error.toString()
  } else if (error instanceof Error) {
    alertTitle = error.name
    alertMsg = error.message
  }

  captureException(cleaned)
  console.error(error)
  toastAlert("error", alertTitle, alertMsg)
}

type CheckWrapper<T> =
  | {
      ok: true
      value: T
    }
  | {
      ok: false
    }

export async function checkException<T>(
  callback: (...params: unknown[]) => Promise<T>
): Promise<CheckWrapper<T>> {
  try {
    const result = await callback()
    return { ok: true, value: result }
  } catch (e) {
    reportAndRecover(e, "")
    return { ok: false }
  }
}

interface BaseErrorParameters<T extends string> {
  name: T
  message: string
  recoverable?: boolean
}

export class BaseError<T extends string> extends Error {
  recoverable: boolean

  constructor({ name, message, recoverable }: BaseErrorParameters<T>) {
    super(message)
    this.name = name
    this.recoverable = recoverable ?? false
  }
}

type UnexpectedStateErrorParameters = Omit<
  BaseErrorParameters<"UnexpectedStateError">,
  "name"
>

/**
 * Represents a custom error class for unexpected states that may occur in the application.
 * This error should be thrown when the application encounters a state that it should not have reached.
 * It will be reported to sentry and should be fixed.
 *
 * This will be very similar in utility to `console.assert(false, "message")`.
 * The main difference is that this will break out of the current execution context and report an error to the user.
 */
export class UnexpectedStateError extends BaseError<"UnexpectedStateError"> {
  constructor(params: UnexpectedStateErrorParameters) {
    super({ name: "UnexpectedStateError", ...params })
    Error.captureStackTrace?.(this, UnexpectedStateError)
  }
}

export enum RestErrorType {
  NotFound = "Not Found",
  BadRequest = "Bad Request",
  Unauthorized = "Unauthorized",
  Forbidden = "Forbidden",
}

export type RestOperation = "create" | "read" | "update" | "delete" | "validate"

// omit name while error type is not in use
type RestErrorParameters<T extends string> = Omit<
  BaseErrorParameters<T>,
  "name" | "message"
> &
  RequireAtLeastOne<
    {
      message?: string // use as i18nPath so that it's not outright ignored
      operation: RestOperation
      resource: string
      errorContainer?: ErrorContainer
    },
    "message" | "errorContainer"
  >

type ModelValidationError = RestError & {
  errorContainer: ErrorContainer
} & { code: "validation_error" }

export class RestError extends BaseError<"RestError"> {
  operation: RestOperation
  resource: string
  errorContainer?: ErrorContainer

  /**
   * Creates a new instance of the RestError class.
   * @param operation The REST operation that caused the error.
   * @param resource The resource associated with the error.
   * @param errorContainer The error container object containing the error details.
   */
  constructor({
    operation,
    resource,
    errorContainer,
    recoverable,
    message,
  }: RestErrorParameters<RestErrorType>) {
    super({
      name: "RestError",
      message: message
        ? get(_)(message)
        : errorContainer
          ? errorContainer.message
          : "",
      recoverable,
    })
    this.operation = operation
    this.resource = resource
    this.errorContainer = errorContainer
    Error.captureStackTrace?.(this, RestError)
  }

  get validationErrors(): ErrorDetail[] {
    if (!this.isValidationError()) {
      return []
    }
    return this.errorContainer.errors
  }

  isValidationError(): this is ModelValidationError {
    return this.errorContainer?.code === "validation_error"
  }
}

export function alertRestError(error: RestError) {
  toastAlert(
    "error",
    get(_)("errors.alertTitle"),
    error.errorContainer?.message,
    error.errorContainer?.errors.map(
      (e) => e.message || `${e.domain} ${e.reason}`
    )
  )
}

export function createSessionInvalidationError() {
  return new RestError({
    operation: "validate",
    resource: "Customer",
    recoverable: true,
    message: get(_)(
      "personalInfo.affiliationInformation.sessionInvalidationMessage"
    ),
  })
}

const DUPLICATE_LOGIN_ERROR_CODES = [
  "username_taken",
  "email_taken",
  "email_and_username_taken",
]

export function isDuplicateLoginError(error: ErrorContainer) {
  return DUPLICATE_LOGIN_ERROR_CODES.includes(error.code)
}
